import { useEffect, useState } from 'react';
import { ThemeDataType, TipMetadataInterface } from '@/modules/types/theme';
import { getEnv } from '@/lib/env';
import { appData } from '@/lib/data';
import config from '@/lib/config';

const ThemeStyleID = 'ElearningStyle';

export function useThemeStyle(theme: ThemeDataType | undefined) {
  const [bannerHeight, setBannerHeight] = useState(0);
  const [navHeight] = useState(70); // TO-DO: if we need to calculate height from the file

  useEffect(() => {
    if (!theme) return;

    if (bannerHeight && navHeight && theme && theme.windowMetadata.height) {
      const progressBarHeight = 10;
      const contentHeight =
        theme.windowMetadata.height -
        bannerHeight -
        navHeight -
        progressBarHeight;
      const ThemeStyle = document.getElementById(
        ThemeStyleID,
      ) as HTMLStyleElement;
      if (ThemeStyle) {
        const stylesheet = ThemeStyle.sheet;
        const stylePropertyPrefix = '#ModuleViewer';
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} #content {height:${contentHeight}px;}`,
          0,
        );
      }
    }
  }, [bannerHeight, navHeight, theme]);

  useEffect(() => {
    if (!theme) return;

    const stylePropertyPrefix = '#ModuleViewer';
    const tooltipSelector = `${stylePropertyPrefix} .ui-tooltip-content`;

    if (theme.css) {
      const CustomThemeStyle = document.getElementById(`Custom${ThemeStyleID}`);
      if (CustomThemeStyle) {
        CustomThemeStyle.remove();
      }
      const customStyle = document.createElement('style');
      customStyle.setAttribute('id', `Custom${ThemeStyleID}`);
      document.head.appendChild(customStyle);
      customStyle.textContent = theme.css;
      const customStylesheet = customStyle.sheet;
      if (customStylesheet !== null) {
        const rules = customStylesheet.cssRules;
        Object.entries(rules).forEach(([idx, rule]) => {
          if (rule.toString().search('CSSStyleRule') !== -1) {
            const styleRule = rule as CSSStyleRule;
            styleRule.selectorText = `${stylePropertyPrefix} ${styleRule.selectorText}`;
          }
        });
      }
    }

    const ThemeStyleElement = document.getElementById(ThemeStyleID);
    if (ThemeStyleElement) {
      ThemeStyleElement.remove();
    }
    const style = document.createElement('style');
    style.setAttribute('id', ThemeStyleID);
    document.head.appendChild(style);
    const stylesheet = style.sheet;
    if (theme.windowMetadata) {
      const windowThemeData: any = theme.windowMetadata;
      if (windowThemeData.width)
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} #container {width:${windowThemeData.width}px;}`,
          0,
        );
      if (windowThemeData.height)
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} #container {height:${windowThemeData.height}px;}`,
          0,
        );
      if (windowThemeData.windowBgColor)
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} #content-container {background-color:#${windowThemeData.windowBgColor};}`,
          0,
        );
    }

    setBannerHeight(90);
    (stylesheet as CSSStyleSheet).insertRule(
      `${stylePropertyPrefix} #banner {background:url("${
        getEnv() === 'local' || !theme?.banner
          ? `${config.assetPath}${appData.assets.banner}`
          : theme.banner
      }") no-repeat top left,rgb(255, 255, 255) ;}`,
      0,
    );

    if (theme.bgImg) {
      (stylesheet as CSSStyleSheet).insertRule(
        `${stylePropertyPrefix} #content-container {background:url("${theme.bgImg}") repeat-y center;}`,
        0,
      );
    }

    if (theme.bodyMetadata) {
      const bodyThemeData: any = theme.bodyMetadata;
      const bodySelector = `${stylePropertyPrefix} .canvas-content, ${stylePropertyPrefix} .canvas-content p, ${stylePropertyPrefix} .canvas-content ul, ${stylePropertyPrefix} .canvas-content ol, .mceItemTable, .mceItemTable p, .mceItemTable ul, .mceItemTable ol`;
      if (bodyThemeData.bodyFont) {
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} .body-font-items, ${bodySelector}, ${tooltipSelector} {font-family:${bodyThemeData.bodyFont};}`,
          0,
        );
      }
      if (bodyThemeData.bodySize)
        (stylesheet as CSSStyleSheet).insertRule(
          `${bodySelector} {font-size:${bodyThemeData.bodySize};}`,
          0,
        );
      if (bodyThemeData.bodyColor)
        (stylesheet as CSSStyleSheet).insertRule(
          `${bodySelector} {color:#${bodyThemeData.bodyColor};}`,
          0,
        );
      if (bodyThemeData.bodyFontWeight)
        (stylesheet as CSSStyleSheet).insertRule(
          `${bodySelector}, ${tooltipSelector} {font-weight:${bodyThemeData.bodyFontWeight};}`,
          0,
        );
    }
    if (theme.headingMetadata) {
      const headingThemeData: any = theme.headingMetadata;
      if (headingThemeData.heading1Font)
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} h1 {font-family:${headingThemeData.heading1Font};}`,
          0,
        );
      if (headingThemeData.heading1Color)
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} h1 {color:#${headingThemeData.heading1Color};}`,
          0,
        );
      if (headingThemeData.heading1Size)
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} h1 {font-size:${headingThemeData.heading1Size};}`,
          0,
        );
      if (headingThemeData.heading2Font)
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} h2 {font-family:${headingThemeData.heading2Font};}`,
          0,
        );
      if (headingThemeData.heading2Color)
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} h2 {color:#${headingThemeData.heading2Color};}`,
          0,
        );
      if (headingThemeData.heading2Size)
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} h2 {font-size:${headingThemeData.heading2Size};}`,
          0,
        );
    }
    if (theme.menuMetadata) {
      const menuThemeData: any = theme.menuMetadata;
      if (menuThemeData.menuColor1) {
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} .navigation-background {background-color:#${menuThemeData.menuColor1} !important;}`,
          0,
        );
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} #tip-bar a.img-btn, ${stylePropertyPrefix} #content-container .btn, ${stylePropertyPrefix} #do-assessment, ${stylePropertyPrefix} button.elmo-btn--primary {background:#${menuThemeData.menuColor1} !important;}`,
          0,
        );
      }
      if (menuThemeData.buttonColor) {
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} #tip-bar a.img-btn, ${stylePropertyPrefix} #content-container .btn, ${stylePropertyPrefix} #do-assessment, ${stylePropertyPrefix} button.elmo-btn--primary {color:#${menuThemeData.buttonColor} !important;}`,
          0,
        );
      }
      if (
        menuThemeData.isGradientMenu &&
        menuThemeData.menuColor1 &&
        menuThemeData.menuColor2
      ) {
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} #navigation {
              height: 70px; /* @todo will be replaced by the height of the image from the theme naviBg */
              background-image: -webkit-gradient(
                linear,
                left top,
                left bottom,
                color-stop(0, #${menuThemeData.menuColor1}),
                color-stop(1, #${menuThemeData.menuColor2})
              );
              background-image: -o-linear-gradient(bottom, #${menuThemeData.menuColor1} 0%, #${menuThemeData.menuColor2} 100%);
              background-image: -moz-linear-gradient(bottom, #${menuThemeData.menuColor1} 0%, #${menuThemeData.menuColor2} 100%);
              background-image: -webkit-linear-gradient(bottom, #${menuThemeData.menuColor1} 0%, #${menuThemeData.menuColor2} 100%);
              background-image: -ms-linear-gradient(bottom, #${menuThemeData.menuColor1} 0%, #${menuThemeData.menuColor2} 100%);
              background-image: linear-gradient(to bottom, #${menuThemeData.menuColor1} 0%, #${menuThemeData.menuColor2} 100%);          
            }`,
          0,
        );
      } else {
        (stylesheet as CSSStyleSheet).insertRule(
          `${stylePropertyPrefix} #navigation {background-color:#${menuThemeData.menuColor1};}`,
          0,
        );
      }
    }
    if (theme.tipMetadata) {
      const tooltipThemeData: TipMetadataInterface = theme.tipMetadata;
      if (tooltipThemeData.tipboxColor)
        (stylesheet as CSSStyleSheet).insertRule(
          `${tooltipSelector} {color:#${tooltipThemeData.tipboxColor};}`,
          0,
        );
      if (tooltipThemeData.tipboxBg) {
        (stylesheet as CSSStyleSheet).insertRule(
          `${tooltipSelector} {background:#${tooltipThemeData.tipboxBg};} `,
          0,
        );
        (stylesheet as CSSStyleSheet).insertRule(
          `${tooltipSelector}.place-top::after{border-top-color:#${tooltipThemeData.tipboxBg};}`,
          0,
        );
        (stylesheet as CSSStyleSheet).insertRule(
          `${tooltipSelector}.place-bottom::after{border-bottom-color:#${tooltipThemeData.tipboxBg};}`,
          0,
        );
        (stylesheet as CSSStyleSheet).insertRule(
          `${tooltipSelector}.place-right::after{border-right-color:#${tooltipThemeData.tipboxBg};}`,
          0,
        );
        (stylesheet as CSSStyleSheet).insertRule(
          `${tooltipSelector}.place-left::after{border-left-color:#${tooltipThemeData.tipboxBg};}`,
          0,
        );
      }
      if (tooltipThemeData.tipboxSize)
        (stylesheet as CSSStyleSheet).insertRule(
          `${tooltipSelector} .qtip-content {font-size:${tooltipThemeData.tipboxSize};}`,
          0,
        );
    }
  }, [theme]);

  return null;
}
